/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable max-len */
import { motion, useTransform } from 'framer-motion';
import { EventsHero } from '../blocks/EventsHero';
import { InputType } from '../../types';
import { CTATileCarousel } from '../blocks/CTATileCarousel';
import LogoCardGrid from '../blocks/LogoCardGrid';
import { HalfImgHero, ImageColumns } from '../blocks';
import MeetTheTeam from '../blocks/MeetTheTeam';
import { ModalForm } from '../forms/ModalForm';
import { FaqBlock } from '../FaqBlock';
import { TextBlock } from '../TextBlock';
import Disclaimer from '../Disclaimer';
import { MotionImage, StackedContent, useInViewScroll, XumoButton as Button } from 'ui';

const CreditsTileScrollerRow = ({ images, x, variants, childVariants }: any) => (
  <motion.div style={{ x }} className="relative flex space-x-2 xl:space-x-4">
    <div className="invisible inline-block aspect-[16/7] w-[45vw] max-w-[400px] rounded-xl shadow-xl xl:w-[20vw] " />
    <div className="space absolute -left-2 flex -translate-x-full space-x-2 xl:-left-4 xl:space-x-4">
      {images.map((image: string, index: number) => (
        <motion.div
          key={index + image}
          variants={childVariants}
          className="relative inline-block aspect-[16/7] w-[45vw] max-w-[400px] overflow-hidden rounded-xl shadow-xl xl:w-[20vw]"
        >
          <MotionImage key={'img' + index + image} src={image} alt="" fill className="object-cover" sizes="40vw" />
        </motion.div>
      ))}
    </div>
    <motion.div className="absolute left-0 flex flex-row space-x-2 xl:space-x-4">
      {images.map((image: string, index: number) => (
        <motion.div
          key={'alt' + index + image}
          variants={childVariants}
          className="relative inline-block aspect-[16/7] w-[45vw] max-w-[400px] overflow-hidden rounded-xl shadow-xl xl:w-[20vw]"
        >
          <MotionImage key={'alt-img' + index + image} src={image} alt="" fill className="object-cover" sizes="40vw" />
        </motion.div>
      ))}
    </motion.div>
  </motion.div>
);

const CreditsTileScroller = ({ bg, heading, body, icons, cta, callout }: any) => {
  const { ref, progress } = useInViewScroll([0, 1], ['start 100vh', 'end 0vh']);
  const ctas = Array.isArray(cta) ? cta : cta ? [cta] : [];

  const scrollX = useTransform(progress, [0, 1], ['-15vw', '15vw']);
  const scrollXAlt = useTransform(progress, [0, 1], ['15vw', '-15vw']);

  const variants = {
    visible: {
      transition: {
        delayChildren: 0,
        staggerChildren: 0.02,
        restDelta: 0.0001,
        restSpeed: 0.0001,
      },
    },
  };

  const childVariants = {
    initial: {
      y: 100,
      opacity: 0.0,
    },
    visible: {
      y: 0,
      opacity: [0, 1],
      transition: {
        ease: [0, 1, 1, 1],
        duration: 1.5,
        restDelta: 0.00001,
        restSpeed: 0.00001,
      },
    },
  };

  return (
    <motion.section ref={ref} className="relative z-40 flex w-full justify-center bg-white" data-testid={'text-section'}>
      <div className="wrapper">
        <div className={`${bg} relative space-y-16 py-16 md:pt-24`}>
          <div className="flex flex-col items-center justify-center">
            <StackedContent full center noOrphans content={[{ heading }, { body, className: 'xl:!text-[32px]' }]} />
            {callout && (
              <div className="mt-10 flex flex-col items-center justify-center gap-4 text-center sm:flex-row">
                <span className="relative block h-10 w-10 shrink-0">
                  <MotionImage src={callout.icon} alt={callout.iconAlt || ''} fill sizes="40px" className="object-contain" />
                </span>
                <p className="type-body-uniform m-0 text-center sm:text-left" dangerouslySetInnerHTML={{ __html: callout.text }} />
              </div>
            )}
          </div>

          <div className="relative">
            <motion.div
              initial="initial"
              whileInView="visible"
              variants={variants}
              viewport={{ once: true, margin: '-20% 0% -20% 0%' }}
              className="absolute left-0 w-screen space-y-2 xl:space-y-4"
            >
              {icons.map((images: string[], index: number) => (
                <CreditsTileScrollerRow
                  key={index}
                  images={images}
                  x={index % 2 === 0 ? scrollX : scrollXAlt}
                  variants={variants}
                  childVariants={childVariants}
                />
              ))}
            </motion.div>
            {icons.map((_: string[], index: number) => (
              <div key={index} className="invisible aspect-[16/7] w-[45vw] max-w-[400px] rounded-xl shadow-xl xl:w-[20vw] " />
            ))}
          </div>

          {ctas.length > 0 && (
            <div className="flex flex-col items-center justify-center gap-4 md:flex-row">
              {ctas.map((button, index) => (
                <Button key={button.href || button.label || index} {...button} />
              ))}
            </div>
          )}
        </div>
      </div>
    </motion.section>
  );
};

export const CreditsPage = () => {
  return (
    <>
      <EventsHero type="credits" bgColor="white" textColor="black" title={'<span>It </span>  <strong>pays to stream</strong><br />  <span>with Xumo</span>'} />
      <TextBlock
        bg="white"
        sectionBg="white"
        content={[
          {
            body: 'Save on <strong class="font-semibold">your pick of 100+ subscriptions</strong> and purchases through your Xumo account with internet and Xumo Stream Box from Mediacom.',
            className:
              'weight-300 font-WorkSans text-[32px] leading-[1.2] tracking-tight text-xumoDarkPlum sm:text-[32px] md:text-[40px] lg:text-[48px] 2xl:text-[52px]',
          },
        ]}
      />
      <CreditsTileScroller
        bg="bg-[#EFF3F5]"
        heading="Choose from <br><strong>100+ apps and Xumo <br>Premium Channels</strong>"
        body="Explore Xumo Store on your device to find apps, channels and subscriptions your credits can be applied to."
        icons={[
          [
            '/static/images/refresh/2026/credits/images/app_logos/amc+.png',
            '/static/images/refresh/2026/credits/images/app_logos/Vix.png',
            '/static/images/refresh/2026/credits/images/app_logos/Peacock.png',
            '/static/images/refresh/2026/credits/images/app_logos/MGM+.png',
            '/static/images/refresh/2026/credits/images/app_logos/amc+.png',
            '/static/images/refresh/2026/credits/images/app_logos/Vix.png',
            '/static/images/refresh/2026/credits/images/app_logos/Peacock.png',
            '/static/images/refresh/2026/credits/images/app_logos/MGM+.png',
          ],
          [
            '/static/images/refresh/2026/credits/images/app_logos/NBA_league_pass.png',
            '/static/images/refresh/2026/credits/images/app_logos/Apple.png',
            '/static/images/refresh/2026/credits/images/app_logos/HBOMAX.png',
            '/static/images/refresh/2026/credits/images/app_logos/Hallmark.png',
            '/static/images/refresh/2026/credits/images/app_logos/NBA_league_pass.png',
            '/static/images/refresh/2026/credits/images/app_logos/Apple.png',
            '/static/images/refresh/2026/credits/images/app_logos/HBOMAX.png',
            '/static/images/refresh/2026/credits/images/app_logos/Hallmark.png',
          ],
        ]}
        cta={{
          label: 'Subscribe through Xumo',
          href: 'https://www.xumo.com/subscriptions',
          target: '_blank',
        }}
        callout={{
          icon: '/static/images/refresh/2026/credits/images/voice_icons.svg',
          text: 'Say “Xumo Store” into your voice remote',
        }}
      />
      <ImageColumns
        type={2}
        title={[{ heading: 'How to <strong>get credits</strong>' }]}
        columns={[
          {
            body: '<span  class="font-semibold">Sign up</span> for Xumo Stream Box from Mediacom',
            image: '/static/images/refresh/2026/credits/images/Sign_up_icon.png',
          },
          {
            body: '<span class="font-semibold">Activate</span> your device and start streaming with Xumo',
            image: '/static/images/refresh/2026/credits/images/Activate icon.png',
          },
          {
            body: '<span class="font-semibold">Get $100</span> in credits applied to future purchases with your Xumo account',
            image: '/static/images/refresh/2026/credits/images/Money icon.png',
          },
        ]}
        cta={{
          label: 'Get started with Mediacom',
          href: 'https://mediacomcable.com/television/xumo',
          target: '_blank',
        }}
      />
      <TextBlock
        bg="bg-[#F6F8F9]"
        sectionBg="bg-[#F6F8F9]"
        content={[
          { heading: 'All <strong>about credits</strong>' },
          {
            body: 'Find out how credits work and how you can save on streaming with Xumo.',
            className: 'weight-400 font-WorkSans text-[18px] leading-[1.25] tracking-tight sm:text-[18px] md:text-[20px] lg:text-[24px] xl:text-[32px]',
          },
          {
            ctaRow: [
              {
                label: 'Learn more',
                href: 'https://xumo.com/support/credits ',
                target: '_blank',
                className: 'mt-4',
              },
            ],
          },
        ]}
      />

      <FaqBlock
        bg="bg-white"
        heading="Frequently asked questions about Xumo Credits"
        questions={[
          {
            question: 'What are Xumo Credits?',
            answer:
              'Xumo Credits are savings that are automatically applied toward streaming subscriptions and content purchases made through your Xumo account — so you can lower your costs and watch even more.',
          },
          {
            question: 'How do I get this offer?',
            answer:
              'To get this offer, <a href="https&#58;//mediacomcable.com/television/xumo" target="_blank" rel="noopener noreferrer" class="text-[#BB2E76] font-semibold underline">sign up</a> for Xumo Stream Box through Mediacom, then activate your device. During setup, you’ll be able to create a Xumo account. Once you’ve activated your Xumo Stream Box and created your Xumo account, $100 in Xumo Credits will be added to your Xumo account within 24 hours of activation.',
          },
          {
            question: 'How do I know if I qualify for this offer?',
            answer:
              'If you are a Mediacom customer who signed up for a WiFi package that includes Xumo Stream Box, you may qualify for this offer when you receive your Xumo device. If you are an existing Mediacom customer who has received, but not yet activated your Xumo Stream Box, you may qualify for this offer. <br><br>To confirm your eligibility, visit <a href="https&#58;//mediacomcable.com/television/xumo" target="_blank" rel="noopener noreferrer" class="text-[#BB2E76] font-semibold underline">mediacomcable.com/television/xumo</a> and log in or select <span class="weight-600">Shop Now</span> and enter your address.',
          },
          {
            question: 'If I already have a Xumo Stream Box or Xumo account, do I qualify for Xumo Credits?',
            answer:
              'Only Mediacom Xumo customers who activate their Xumo Stream Box and create a Xumo account between June 1, 2026 and October 31, 2026 will qualify.',
          },
          {
            question: 'How do I know if Xumo Credits have been added to my account?',
            answer:
              'You’ll receive an email notification when credits are added to your account. You can also view your credits balance by going to the <strong>Subscriptions</strong> tab in your Xumo account dashboard. Go to <strong>Your Xumo Credits</strong>, which will display your credit balance. You can also check the <strong>Transactions</strong> tab to see if and when you received Xumo Credits, as well as their expiration date.',
          },
          {
            question: 'What can I use my credits on?',
            answer:
              'Xumo Credits can be applied toward subscription costs of 100+ apps and channels purchased through your Xumo account, like Apple TV, HBO Max, Peacock, AMC+, MGM+ and more. <br><br>Browse select apps at <a href="https&#58;//xumo.com/subscriptions" target="_blank" rel="noopener noreferrer" class="text-[#BB2E76] font-semibold underline">xumo.com/subscriptions</a> and view all eligible apps in Xumo Store on your device. Say “Xumo Store” into your voice remote or select the Store tile on your home screen to see what’s available. <br><br>Credits can only be applied to purchases made through Xumo. Purchases within third party apps are not eligible.',
          },
        ]}
      />
    </>
  );
};
